import { Rows2, Rows3 } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import type { Density } from './types';

export interface DensityToggleProps {
  density: Density;
  onChange: (next: Density) => void;
}

/**
 * Two-button segmented control for row density. The chosen state shows
 * the active icon on a soft mint pill so it reads as the *current* density
 * rather than a generic toggle target — gives the eye the answer faster
 * than the previous single-button flip.
 */
export function DensityToggle({ density, onChange }: DensityToggleProps) {
  return (
    <TooltipProvider delayDuration={400}>
      <div className="flex h-9 items-center overflow-hidden rounded-md bg-neutral-50/70 p-0.5 ring-1 ring-(--color-border)">
        <SegButton
          active={density === 'comfortable'}
          label="Comfortable rows"
          onClick={() => {
            onChange('comfortable');
          }}
          icon={<Rows3 className="h-3.5 w-3.5" strokeWidth={1.75} />}
        />
        <SegButton
          active={density === 'dense'}
          label="Dense rows"
          onClick={() => {
            onChange('dense');
          }}
          icon={<Rows2 className="h-3.5 w-3.5" strokeWidth={1.75} />}
        />
      </div>
    </TooltipProvider>
  );
}

function SegButton({
  active,
  label,
  onClick,
  icon,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={active}
          onClick={onClick}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded transition-colors duration-150',
            'focus-visible:shadow-(--shadow-focus-ring-solid) focus-visible:outline-none',
            active
              ? 'bg-surface text-primary-700 shadow-xs'
              : 'text-neutral-500 hover:text-neutral-800',
          )}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}
